import type { MrgReportListParams } from '../types/pages'
/**
 * MRG Report — React parity with Angular `ReportsResidentsClaimsComponent`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  DollarSign,
  Download,
  Loader2,
  Percent,
  Receipt,
  Scale,
} from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { ListColumnsMenu } from '@/components/ui/ListColumnsMenu'
import { ListSearchToolbar } from '@/components/ui/ListSearchToolbar'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { useConfigurableListColumns } from '@/hooks/useConfigurableListColumns'
import { useUserRole } from '@/hooks/useUserRole'
import { ListExportStatusCard } from '@/features/reimbursements/components/ListExportStatusCard'
import { showApiError } from '@/lib/utils/notifications'
import { formatAmount } from '@/lib/utils/formatters'
import { cn } from '@lib/utils/helpers'
import { ReportOverviewMetrics } from '../components/ReportOverviewMetrics'
import { ReportTableScroll } from '../components/ReportTableScroll'
import reportStyles from '../styles/reportsTable.module.css'
import { REPORT_EXPORT_BUTTON_CLASS } from '../constants/configuredReportStyles'
import {
  MrgReportFilters,
  type MrgReportFilterState,
} from '../components/MrgReportFilters'
import { useReportExportPolling } from '../hooks/useReportExportPolling'
import { getMrgReportList } from '../services/reportsService'
import type { MrgReportListResponse, MrgReportRow } from '../types/api'
import { buildMrgReportExportParams } from '../utils/reportExportUrls'
import { appendMrgReportListParams } from '../utils/reportQueryParams'
import {
  buildMrgReportSessionFromParams,
  clearMrgReportSession,
  readMrgReportSession,
  sessionFilterToMrgReportPatch,
  writeMrgReportSession,
} from '../utils/mrgReportSessionFilter'
import { isWeekSummaryRow } from '../utils/mrgReportRowFormatters'
import {
  DEFAULT_PARAMS,
  EXPORT_REPORT_TYPE,
  MRG_COLUMNS,
} from '../constants/pages/mrgReportPage'
import {
  MRG_REPORT_COLUMN_OPTIONS,
  MRG_REPORT_COLUMN_VISIBILITY_STORAGE_KEY,
  MRG_REPORT_LOCKED_COLUMN_IDS,
} from '../constants/listColumns'


function initialParams(): MrgReportListParams {
  const session = readMrgReportSession()
  if (session) {
    return { ...DEFAULT_PARAMS, ...sessionFilterToMrgReportPatch(session) }
  }
  return { ...DEFAULT_PARAMS }
}

function countActiveMrgFilters(f: MrgReportFilterState): number {
  let n = 0
  if (f.q.trim()) n++
  if (f.company_id) n++
  if (f.property_id) n++
  if (f.fromDt || f.toDt) n++
  if (f.claimfromDt || f.claimtoDt) n++
  if (f.rentalStatus.length) n++
  return n
}

function canExpandRow(row: MrgReportRow): boolean {
  return !row.PaymentSource?.includes('Week')
}

function visibleChildRows(
  row: MrgReportRow,
  expandedPolicyId: string | null,
  showAllDetails: boolean,
): MrgReportRow[] {
  if (!row.details?.length) return []
  const pid = String(row.policy_id ?? '')
  if (showAllDetails) return row.details
  if (expandedPolicyId === pid) {
    return row.details.filter((d) => String(d.child_policy_id ?? '') === pid)
  }
  return []
}

export function MrgReportPage() {
  const { companyId, propertyId } = useUserRole()
  const [params, setParams] = useState<MrgReportListParams>(initialParams)
  const [searchDraft, setSearchDraft] = useState(() => params.q)
  const [rows, setRows] = useState<MrgReportRow[]>([])
  const [summary, setSummary] = useState<MrgReportListResponse['total_count']>()
  const [pageMeta, setPageMeta] = useState({ current: 1, last: 1, total: 0 })
  const [loading, setLoading] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [expandedPolicyId, setExpandedPolicyId] = useState<string | null>(null)
  const [showAllDetails, setShowAllDetails] = useState(false)

  const columnsController = useConfigurableListColumns({
    storageKey: MRG_REPORT_COLUMN_VISIBILITY_STORAGE_KEY,
    columnOptions: MRG_REPORT_COLUMN_OPTIONS,
    lockedColumnIds: [...MRG_REPORT_LOCKED_COLUMN_IDS],
  })
  const visibleMrgColumns = useMemo(() => {
    const byKey = new Map(MRG_COLUMNS.map((column) => [column.key, column]))
    return columnsController.tableColumnIds
      .map((columnId) => byKey.get(columnId))
      .filter((column): column is (typeof MRG_COLUMNS)[number] => Boolean(column))
  }, [columnsController.tableColumnIds])

  const exportPolling = useReportExportPolling(EXPORT_REPORT_TYPE)

  useEffect(() => {
    if (companyId && !params.company_id) {
      setParams((p) => ({ ...p, company_id: String(companyId) }))
    }
    if (propertyId && !params.property_id) {
      setParams((p) => ({ ...p, property_id: String(propertyId) }))
    }
  }, [companyId, propertyId, params.company_id, params.property_id])

  useEffect(() => {
    setSearchDraft(params.q)
  }, [params.q])

  useEffect(() => {
    const timer = setTimeout(() => {
      const nextQ = searchDraft.trim()
      if (nextQ !== params.q.trim()) {
        setParams((prev) => ({ ...prev, q: nextQ, page: 1 }))
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [searchDraft, params.q])

  const filterState: MrgReportFilterState = useMemo(
    () => ({
      q: params.q,
      company_id: params.company_id,
      company_name: params.company_name,
      property_id: params.property_id,
      property_name: params.property_name,
      fromDt: params.fromDt,
      toDt: params.toDt,
      claimfromDt: params.claimfromDt,
      claimtoDt: params.claimtoDt,
      rentalStatus: params.rentalStatus,
    }),
    [params],
  )

  const fetchList = useCallback(async () => {
    setLoading(true)
    try {
      const qs = new URLSearchParams()
      appendMrgReportListParams(qs, params)
      writeMrgReportSession(buildMrgReportSessionFromParams(params))
      const res = await getMrgReportList(qs)
      setRows(res.weekdata ?? [])
      setSummary(res.total_count)
      setPageMeta({ current: res.current, last: res.last, total: res.total })
    } catch (e) {
      showApiError(e)
    } finally {
      setLoading(false)
    }
  }, [params])

  useEffect(() => {
    void fetchList()
  }, [fetchList])

  const handleSort = (orderBy: string) => {
    setParams((p) => ({
      ...p,
      page: 1,
      orderBy,
      sortBy: p.sortBy === 'asc' ? 'desc' : 'asc',
    }))
  }

  const handleExport = async () => {
    const qs = buildMrgReportExportParams(params)
    const path = `/api/export/insurance_report?${qs.toString()}&view=xlsx`
    await exportPolling.triggerExport(path, qs)
  }

  return (
    <div data-testid="mrg-report-page">
      <PageShell
        title="MRG Report"
        pageHeaderTestIdRoot="mrg-report-page-header"
        contentClassName="!space-y-6 w-full max-w-none min-w-0"
      >
        <ListSearchToolbar
          searchValue={searchDraft}
          searchPlaceholder="Search applicants…"
          onSearchChange={setSearchDraft}
          activeFilterCount={countActiveMrgFilters(filterState)}
          onOpenFilters={() => setFiltersOpen(true)}
          testIdPrefix="mrg-report"
          className={exportPolling.status ? 'mb-20' : undefined}
          actions={
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                className={REPORT_EXPORT_BUTTON_CLASS}
                disabled={
                  exportPolling.triggering || exportPolling.isProcessing
                }
                onClick={() => void handleExport()}
              >
                {exportPolling.triggering || exportPolling.isProcessing ? (
                  <Loader2 className="h-[18px] w-[18px] animate-spin" />
                ) : (
                  <Download className="h-[18px] w-[18px]" />
                )}
                Export
              </button>
              <ListColumnsMenu
                columns={columnsController}
                testIdPrefix="mrg-report"
              />
            </div>
          }
          actionsOverlay={
            exportPolling.status ? (
              <ListExportStatusCard
                status={{
                  status: exportPolling.status.status ?? '',
                  file_path: exportPolling.status.file_path,
                  created_at: exportPolling.status.created_at,
                }}
                isBusy={
                  exportPolling.isProcessing || exportPolling.triggering
                }
                onDismiss={() => exportPolling.dismissStatus()}
                testId="mrg-report-export-status"
                className="absolute top-[calc(100%+0.625rem)] left-0 right-0 z-[5]"
              />
            ) : null
          }
        />

        <MrgReportFilters
          filters={filterState}
          isOpen={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          onApply={(next) => setParams((p) => ({ ...p, ...next, page: 1 }))}
          onReset={() => {
            clearMrgReportSession()
            setSearchDraft('')
            setParams({
              ...DEFAULT_PARAMS,
              company_id: companyId ? String(companyId) : '',
              property_id: propertyId ? String(propertyId) : '',
            })
          }}
        />

        {summary ? (
          <ReportOverviewMetrics
            title="MRG overview"
            subtitle="Current report summary"
            testIdPrefix="mrg-report"
            loading={loading}
            items={[
              {
                id: 'total-paid',
                label: 'Total Paid',
                value: formatAmount(summary.rent_amount),
                icon: <DollarSign className="h-6 w-6" aria-hidden />,
                tone: 'blue',
              },
              {
                id: 'mrg-premium',
                label: 'Total MRG Premium',
                value: formatAmount(summary.mgr_premium),
                icon: <Percent className="h-6 w-6" aria-hidden />,
                tone: 'violet',
              },
              {
                id: 'reimbursements-paid',
                label: 'Total Reimbursements Paid',
                value: formatAmount(summary.default_paid),
                icon: <Receipt className="h-6 w-6" aria-hidden />,
                tone: 'green',
              },
              {
                id: 'remaining-balance',
                label: 'Remaining Balance',
                value: formatAmount(summary.remaining_balance),
                icon: <Scale className="h-6 w-6" aria-hidden />,
                tone: 'sky',
              },
            ]}
          />
        ) : null}

        <div className={reportStyles.tableCard}>
          <ListPaginationBar
            placement="header"
            currentPage={pageMeta.current}
            lastPage={pageMeta.last}
            totalRecords={pageMeta.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            summaryLayout="angular"
            testIdPrefix="mrg-report"
          />

          {rows.length > 0 && !loading ? (
            <div className="flex justify-end px-4 py-2">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-[#1597ff] shadow-sm transition hover:border-[#1597ff]/40 hover:bg-sky-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1597ff]/30"
                onClick={() => {
                  setShowAllDetails((v) => !v)
                  if (!showAllDetails) setExpandedPolicyId(null)
                }}
              >
                {showAllDetails ? (
                  <ChevronsUpDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
                ) : (
                  <ChevronsDownUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
                )}
                {showAllDetails ? 'Collapse all' : 'Expand all'}
              </button>
            </div>
          ) : null}

          {loading ? (
            <div className="flex min-h-[12rem] items-center justify-center py-16">
              <Loader2
                className="h-8 w-8 animate-spin text-[rgb(21_151_255)]"
                aria-hidden
              />
            </div>
          ) : (
            <ReportTableScroll
              enabled={rows.length > 0}
              testIdPrefix="mrg-report"
            >
              <table
                className={cn(
                  reportStyles.listingTable,
                  'w-full border-collapse text-left text-slate-800',
                  '[&_thead_th]:px-2 [&_tbody_td]:px-2 sm:[&_thead_th]:px-2.5 sm:[&_tbody_td]:px-2.5',
                )}
              >
                <colgroup>
                  <col className={reportStyles.mrgExpandCol} />
                  <col className={reportStyles.mrgPolicyCol} />
                </colgroup>
                <thead>
                  <tr>
                    <th
                      className={cn(
                        reportStyles.actionCell,
                        reportStyles.mrgExpandCol,
                      )}
                      aria-label="Expand or collapse"
                    />
                    <th className={cn(reportStyles.mrgPolicyCol, 'text-left')}>
                      Policy
                    </th>
                    {visibleMrgColumns.map((col) => {
                      const alignClass =
                        col.align === 'right'
                          ? 'text-right'
                          : col.align === 'center'
                            ? 'text-center'
                            : 'text-left'
                      return (
                        <th
                          key={col.key}
                          className={cn(alignClass, col.headerClassName)}
                        >
                          {col.sortable ? (
                            <button
                              type="button"
                              className={cn(
                                'inline-flex items-center gap-1.5 whitespace-nowrap font-semibold text-slate-600 hover:text-slate-900',
                                col.align === 'right' && 'ml-auto flex-row-reverse',
                                params.orderBy === col.key &&
                                  'text-[rgb(21_151_255)]',
                              )}
                              onClick={() => handleSort(col.key)}
                            >
                              <span>{col.label}</span>
                              <span
                                className={cn(
                                  'inline-flex items-center text-[11px]',
                                  params.orderBy === col.key
                                    ? 'text-[#1597ff]'
                                    : 'text-slate-400',
                                )}
                              >
                                {params.orderBy === col.key ? (
                                  params.sortBy === 'asc' ? (
                                    '▲'
                                  ) : (
                                    '▼'
                                  )
                                ) : (
                                  <ChevronsUpDown
                                    className="h-3 w-3 shrink-0"
                                    aria-hidden
                                  />
                                )}
                              </span>
                            </button>
                          ) : (
                            <span
                              className={cn(
                                'whitespace-nowrap font-semibold text-slate-600',
                                col.align === 'right' && 'block w-full text-right',
                              )}
                            >
                              {col.label}
                            </span>
                          )}
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={visibleMrgColumns.length + 2}
                        className="py-12 text-center text-slate-500"
                      >
                        No records found
                      </td>
                    </tr>
                  ) : (
                    rows.flatMap((row, idx) => {
                      const pid = String(row.policy_id ?? idx)
                      const children = visibleChildRows(
                        row,
                        expandedPolicyId,
                        showAllDetails,
                      )
                      const isExpanded =
                        showAllDetails || expandedPolicyId === pid
                      const main = (
                        <tr
                          key={`${pid}-main`}
                          className={
                            isWeekSummaryRow(row) ? 'font-semibold' : ''
                          }
                        >
                          <td
                            className={cn(
                              reportStyles.actionCell,
                              reportStyles.mrgExpandCol,
                            )}
                          >
                            {canExpandRow(row) ? (
                              <button
                                type="button"
                                className="inline-flex rounded p-1 text-slate-600 hover:bg-slate-100"
                                onClick={() =>
                                  setExpandedPolicyId((cur) => {
                                    setShowAllDetails(false)
                                    return cur === pid ? null : pid
                                  })
                                }
                                aria-label={
                                  isExpanded
                                    ? 'Collapse details'
                                    : 'Expand details'
                                }
                              >
                                {isExpanded ? (
                                  <ChevronDown
                                    className="h-4 w-4"
                                    aria-hidden
                                  />
                                ) : (
                                  <ChevronRight
                                    className="h-4 w-4"
                                    aria-hidden
                                  />
                                )}
                              </button>
                            ) : null}
                          </td>
                          <td
                            className={cn(
                              reportStyles.mrgPolicyCol,
                              'font-medium text-slate-800',
                            )}
                          >
                            <span className={reportStyles.mrgPolicyColInner}>
                              {row.policy_id ?? '—'}
                            </span>
                          </td>
                          {visibleMrgColumns.map((col) => (
                            <td
                              key={col.key}
                              className={cn(
                                col.className,
                                isWeekSummaryRow(row) &&
                                  [
                                    'applicant_id',
                                    'application_id',
                                    'rent_amount',
                                    'paid',
                                    'mgr_premium',
                                  ].includes(col.key) &&
                                  'font-semibold',
                              )}
                            >
                              {col.render(row)}
                            </td>
                          ))}
                        </tr>
                      )
                      const childRows = children.map((child, cidx) => (
                        <tr
                          key={`${pid}-child-${cidx}`}
                          className="bg-slate-50/80 text-sm"
                        >
                          <td
                            className={cn(
                              reportStyles.actionCell,
                              reportStyles.mrgExpandCol,
                            )}
                          />
                          <td
                            className={cn(
                              reportStyles.mrgPolicyCol,
                              'text-slate-700',
                            )}
                          >
                            <span className={reportStyles.mrgPolicyColInner}>
                              {child.policy_id}
                            </span>
                          </td>
                          {visibleMrgColumns.map((col) => (
                            <td
                              key={col.key}
                              className={cn(
                                col.className,
                                col.key === 'mrg_percentage' && 'font-semibold',
                              )}
                            >
                              {col.render(child)}
                            </td>
                          ))}
                        </tr>
                      ))
                      return [main, ...childRows]
                    })
                  )}
                </tbody>
              </table>
            </ReportTableScroll>
          )}

          <ListPaginationBar
            placement="footer"
            currentPage={pageMeta.current}
            lastPage={pageMeta.last}
            totalRecords={pageMeta.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            summaryLayout="angular"
            testIdPrefix="mrg-report"
          />
        </div>
      </PageShell>
    </div>
  )
}
